import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/errorHandling';
import AmountInput from '../ui/AmountInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import {
  costCentersApi,
  CostCenter,
  CostCenterType,
  CostCenterStatus,
  CreateCostCenterDto,
} from '../../api/costCenters';
import { STANDARD_COST_CENTERS } from '../../content/defaultAccounting';
import {
  costAllocationsApi,
  CostAllocation,
  AllocationBasis,
  AllocationSourceType,
  CreateCostAllocationDto,
} from '../../api/costAllocations';

const emptyCostCenter: CreateCostCenterDto = {
  name: '',
  description: '',
  type: CostCenterType.DIRECT,
  status: CostCenterStatus.ACTIVE,
};

const emptyAllocation: CreateCostAllocationDto = {
  costCenterId: '',
  sourceType: AllocationSourceType.MANUAL,
  sourceId: '',
  amount: 0,
  basis: AllocationBasis.FIXED_AMOUNT,
  description: '',
};

const statusBadge: Record<CostCenterStatus, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  INACTIVE: 'bg-amber-100 text-amber-800',
  ARCHIVED: 'bg-slate-200 text-slate-600',
};

const typeBadge: Record<CostCenterType, string> = {
  DIRECT: 'bg-cyan-100 text-cyan-800',
  INDIRECT: 'bg-violet-100 text-violet-800',
  OVERHEAD: 'bg-orange-100 text-orange-800',
};

export default function CostCentersPanel() {
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [totals, setTotals] = useState<Record<string, { totalAllocated: number; allocationCount: number }>>({});
  const [loading, setLoading] = useState(true);

  // Cost center modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CostCenter | null>(null);
  const [form, setForm] = useState<CreateCostCenterDto>(emptyCostCenter);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Allocations drill-down
  const [selected, setSelected] = useState<CostCenter | null>(null);
  const [allocations, setAllocations] = useState<CostAllocation[]>([]);
  const [allocLoading, setAllocLoading] = useState(false);
  const [showAllocModal, setShowAllocModal] = useState(false);
  const [allocForm, setAllocForm] = useState<CreateCostAllocationDto>(emptyAllocation);
  const [allocSaving, setAllocSaving] = useState(false);

  useEffect(() => {
    loadCostCenters();
  }, []);

  const loadCostCenters = async () => {
    try {
      setLoading(true);
      const data = await costCentersApi.getCostCenters();
      setCostCenters(data);
      const summaries = await Promise.all(
        data.map((cc) =>
          costCentersApi
            .getCostCenterSummary(cc.id)
            .then((s) => ({ id: cc.id, totalAllocated: s.totalAllocated, allocationCount: s.allocationCount }))
            .catch(() => ({ id: cc.id, totalAllocated: 0, allocationCount: 0 })),
        ),
      );
      const map: Record<string, { totalAllocated: number; allocationCount: number }> = {};
      summaries.forEach((s) => {
        map[s.id] = { totalAllocated: s.totalAllocated, allocationCount: s.allocationCount };
      });
      setTotals(map);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load cost centers'));
    } finally {
      setLoading(false);
    }
  };

  const handlePopulateDefaults = async () => {
    setSeeding(true);
    try {
      const seeded = await costCentersApi.seedDefaultCostCenters();
      let created = seeded?.created ?? 0;
      if (seeded == null) {
        const existing = await costCentersApi.getCostCenters().catch(() => [] as CostCenter[]);
        const have = new Set(existing.map((c) => c.name.toLowerCase()));
        for (const row of STANDARD_COST_CENTERS) {
          if (have.has(row.name.toLowerCase())) continue;
          await costCentersApi.createCostCenter({ ...row, status: CostCenterStatus.ACTIVE });
          created += 1;
        }
      }
      await loadCostCenters();
      toast.success(created > 0 ? `Added ${created} cost centers` : 'Cost centers are already populated');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not populate cost centers'));
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCostCenter);
    setShowModal(true);
  };

  const openEdit = (cc: CostCenter) => {
    setEditing(cc);
    setForm({
      name: cc.name,
      description: cc.description ?? '',
      type: cc.type,
      status: cc.status,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await costCentersApi.updateCostCenter(editing.id, form);
        toast.success('Cost center updated');
      } else {
        await costCentersApi.createCostCenter(form);
        toast.success('Cost center created');
      }
      setShowModal(false);
      await loadCostCenters();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save cost center'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cc: CostCenter) => {
    if (!window.confirm(`Delete cost center "${cc.name}"? Centers with allocations will be archived instead.`)) {
      return;
    }
    try {
      await costCentersApi.deleteCostCenter(cc.id);
      toast.success('Cost center removed');
      await loadCostCenters();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete cost center'));
    }
  };

  const openAllocations = async (cc: CostCenter) => {
    setSelected(cc);
    setAllocLoading(true);
    try {
      const data = await costAllocationsApi.getCostAllocations({ costCenterId: cc.id });
      setAllocations(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load allocations'));
    } finally {
      setAllocLoading(false);
    }
  };

  const refreshAllocations = async (costCenterId: string) => {
    const data = await costAllocationsApi.getCostAllocations({ costCenterId });
    setAllocations(data);
  };

  const openCreateAllocation = () => {
    if (!selected) return;
    setAllocForm({ ...emptyAllocation, costCenterId: selected.id });
    setShowAllocModal(true);
  };

  const handleSaveAllocation = async () => {
    if (!selected) return;
    if (!allocForm.sourceId.trim()) {
      toast.error('Source reference is required');
      return;
    }
    if (!allocForm.amount || allocForm.amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    setAllocSaving(true);
    try {
      await costAllocationsApi.createCostAllocation({ ...allocForm, costCenterId: selected.id });
      toast.success('Allocation added');
      setShowAllocModal(false);
      await refreshAllocations(selected.id);
      await loadCostCenters();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to add allocation'));
    } finally {
      setAllocSaving(false);
    }
  };

  const handleDeleteAllocation = async (allocation: CostAllocation) => {
    if (!selected) return;
    if (!window.confirm('Delete this allocation?')) return;
    try {
      await costAllocationsApi.deleteCostAllocation(allocation.id);
      toast.success('Allocation removed');
      await refreshAllocations(selected.id);
      await loadCostCenters();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete allocation'));
    }
  };

  const allocationTotal = useMemo(
    () => allocations.reduce((sum, a) => sum + Number(a.amount || 0), 0),
    [allocations],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500" />
      </div>
    );
  }

  // Allocations drill-down view
  if (selected) {
    return (
      <div className="bg-white p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelected(null)}
              className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" /> Back
            </button>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{selected.name}</h3>
              <p className="text-sm text-slate-500">Cost allocations</p>
            </div>
          </div>
          <button onClick={openCreateAllocation} className="btn btn-primary">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" /> Add Allocation
          </button>
        </div>

        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Basis</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                <th className="relative px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allocLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">Loading…</td></tr>
              ) : allocations.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">No allocations yet.</td></tr>
              ) : (
                allocations.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-900">{a.sourceType}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{a.sourceId}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{a.basis}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{a.description || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {a.effectiveDate ? format(new Date(a.effectiveDate), 'MMM dd, yyyy') : format(new Date(a.createdAt), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">{formatCurrency(Number(a.amount || 0))}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleDeleteAllocation(a)} className="text-red-600 hover:text-red-900">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {allocations.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-sm font-medium text-slate-900">Total allocated</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-slate-900">{formatCurrency(allocationTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {showAllocModal && (
          <AllocationModal
            form={allocForm}
            setForm={setAllocForm}
            saving={allocSaving}
            onClose={() => setShowAllocModal(false)}
            onSave={handleSaveAllocation}
          />
        )}
      </div>
    );
  }

  // Cost centers list view
  return (
    <div className="bg-white p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Cost Centers</h3>
          <p className="text-sm text-slate-500">Group costs and track allocations by area of the operation</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" /> New Cost Center
        </button>
      </div>

      {costCenters.length === 0 ? (
        <div className="text-center py-12">
          <BuildingOffice2Icon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-semibold text-slate-900">No cost centers</h3>
          <p className="mt-1 text-sm text-slate-500">
            Load default aviary cost centers, or create your own.
          </p>
          <button
            type="button"
            onClick={handlePopulateDefaults}
            disabled={seeding}
            className="btn btn-primary mt-4 text-sm"
          >
            {seeding ? 'Loading defaults…' : 'Load default cost centers'}
          </button>
        </div>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Allocations</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total Allocated</th>
                <th className="relative px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costCenters.map((cc) => (
                <tr key={cc.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <button onClick={() => openAllocations(cc)} className="text-left">
                      <div className="text-sm font-medium text-cyan-700 hover:text-cyan-900">{cc.name}</div>
                      {cc.description && <div className="text-sm text-slate-500">{cc.description}</div>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${typeBadge[cc.type]}`}>
                      {cc.type.charAt(0) + cc.type.slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge[cc.status]}`}>
                      {cc.status.charAt(0) + cc.status.slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-900">{totals[cc.id]?.allocationCount ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">
                    {formatCurrency(totals[cc.id]?.totalAllocated ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => openEdit(cc)} className="text-cyan-600 hover:text-cyan-900">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(cc)} className="text-red-600 hover:text-red-900">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CostCenterModal
          editing={editing}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function CostCenterModal({
  editing,
  form,
  setForm,
  saving,
  onClose,
  onSave,
}: {
  editing: CostCenter | null;
  form: CreateCostCenterDto;
  setForm: React.Dispatch<React.SetStateAction<CreateCostCenterDto>>;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <BrandModal open onClose={onClose} maxWidthClass="max-w-lg">
      <BrandModalHeader
        title={editing ? 'Edit Cost Center' : 'New Cost Center'}
        onClose={onClose}
      />
      <BrandModalBody>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g. Breeding Aviary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={2}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as CostCenterType }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                {Object.values(CostCenterType).map((t) => (
                  <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as CostCenterStatus }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                {Object.values(CostCenterStatus).map((s) => (
                  <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </BrandModalBody>
      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
        <button type="button" onClick={onSave} disabled={saving} className="btn btn-primary text-sm">
          {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}

function AllocationModal({
  form,
  setForm,
  saving,
  onClose,
  onSave,
}: {
  form: CreateCostAllocationDto;
  setForm: React.Dispatch<React.SetStateAction<CreateCostAllocationDto>>;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <BrandModal open onClose={onClose} maxWidthClass="max-w-lg">
      <BrandModalHeader title="Add Allocation" onClose={onClose} />
      <BrandModalBody>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Source Type</label>
              <select
                value={form.sourceType}
                onChange={(e) => setForm((p) => ({ ...p, sourceType: e.target.value as AllocationSourceType }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                {Object.values(AllocationSourceType).map((s) => (
                  <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Basis</label>
              <select
                value={form.basis}
                onChange={(e) => setForm((p) => ({ ...p, basis: e.target.value as AllocationBasis }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                {Object.values(AllocationBasis).map((b) => (
                  <option key={b} value={b}>{b.replace(/_/g, ' ').toLowerCase()}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Source Reference</label>
            <input
              type="text"
              value={form.sourceId}
              onChange={(e) => setForm((p) => ({ ...p, sourceId: e.target.value }))}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Expense / purchase ID or note"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Amount</label>
              <AmountInput
                value={form.amount}
                onChange={(v) => setForm((p) => ({ ...p, amount: v ?? 0 }))}
                min={0}
                inputClassName="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Percentage (optional)</label>
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.percentage ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, percentage: e.target.value ? parseFloat(e.target.value) : undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Effective Date (optional)</label>
            <input
              type="date"
              value={form.effectiveDate ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, effectiveDate: e.target.value || undefined }))}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
        </div>
      </BrandModalBody>
      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
        <button type="button" onClick={onSave} disabled={saving} className="btn btn-primary text-sm">
          {saving ? 'Saving…' : 'Add'}
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}
